import * as React from 'react'
import { FormEvent } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'

export type TLoginView = {
  error: boolean
  errorMessage?: string
  handleSubmit: () => void
  isLoading: boolean
  setEmail: React.Dispatch<React.SetStateAction<string>>
  setPassword: React.Dispatch<React.SetStateAction<string>>
  email: string
  password: string
}

export const LoginView = ({
  error,
  errorMessage,
  handleSubmit,
  isLoading,
  setEmail,
  setPassword,
  email,
  password
}: TLoginView) => {
  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault()
    handleSubmit()
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ffffff'
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 450,
          px: 4,
          py: 5,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          borderRadius: 2,
          backgroundColor: '#ffffff'
        }}
      >
        {/* Logo */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box
            component="img"
            src="/simbolo.svg"
            alt="Tarken"
            sx={{ 
              height: 56, 
              width: 'auto',
              mb: 2
            }}
          />
        </Box>

        {/* Header */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 600,
              color: 'text.primary',
              mb: 1
            }}
          >
            Bem-vindo!
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'text.secondary',
              fontSize: '0.95rem'
            }}
          >
            Faça login para continuar no HUB Staff
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              borderRadius: 1,
              fontSize: '0.875rem',
              width: '100%'
            }}
          >
            {errorMessage || 'Email ou senha inválidos'}
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleFormSubmit} style={{ width: '100%' }}>
          <TextField
            placeholder="Email *"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            error={error}
            variant="outlined"
            sx={{
              width: '100%',
              mb: 2.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#fff',
                '& fieldset': {
                  borderColor: '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#FFB800',
                  borderWidth: 1,
                },
              },
              '& .MuiInputBase-input': {
                padding: '16px 14px',
              }
            }}
          />

          <TextField
            placeholder="Senha *"
            name="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            error={error}
            variant="outlined"
            sx={{
              width: '100%',
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#fff',
                '& fieldset': {
                  borderColor: '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#FFB800',
                  borderWidth: 1,
                },
              },
              '& .MuiInputBase-input': {
                padding: '16px 14px',
              }
            }}
          />

          <Button
            type="submit"
            variant="contained"
            disabled={!email || !password || isLoading}
            sx={{
              width: '100%',
              py: 1.5,
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              backgroundColor: '#FFB800',
              color: '#000',
              '&:hover': {
                backgroundColor: '#E5A500'
              },
              '&:disabled': {
                backgroundColor: '#E0E0E0',
                color: '#9E9E9E'
              }
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                <CircularProgress size={20} sx={{ color: 'inherit' }} />
                <span>Entrando...</span>
              </Box>
            ) : (
              'ENTRAR'
            )}
          </Button>
        </form>

        {/* Footer */}
        <Typography
          variant="caption"
          sx={{ 
            textAlign: 'center', 
            color: 'text.secondary',
            mt: 2,
            fontSize: '0.75rem'
          }}
        >
          © {new Date().getFullYear()} Tarken Agricultural Intelligence
        </Typography>
      </Paper>
    </Box>
  )
}